import { useStytchMemberSession } from '@stytch/react/b2b';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import {
    QueryDocumentSnapshot,
    FirestoreDataConverter,
    getFirestore,
    serverTimestamp,
    SnapshotOptions,
    connectFirestoreEmulator,
    PartialWithFieldValue,
} from 'firebase/firestore';
import { getAuth, signInWithCustomToken, connectAuthEmulator } from 'firebase/auth';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import config from './config';
import { SnackbarContext } from './Providers/SnackbarProvider';
import useGetToken from './util/useGetToken';

const firebaseConfig = {
    apiKey: config.firebaseApiKey,
    authDomain: config.firebaseAuthDomain,
    projectId: config.firebaseProjectId,
    storageBucket: config.firebaseStorageBucket,
    messagingSenderId: config.firebaseMessagingSenderId,
    appId: config.firebaseAppId,
    measurementId: config.firebaseMeasurementId,
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
export const db = getFirestore(app);
export const auth = getAuth(app);

if (config.isFirebaseEmulated === 'true') {
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectAuthEmulator(auth, 'http://localhost:9099');
}

export function useFirebaseCustomAuth() {
    const { session, fromCache } = useStytchMemberSession();
    const { error } = useContext(SnackbarContext);
    const getToken = useGetToken();

    const [authToken, setAuthToken] = useState<string | null>(null);

    useEffect(() => {
        if (!session || fromCache)
            return; // wait for authentication

        getToken().then(token => {
            if (token)
                setAuthToken(token);
        })

    }, [setAuthToken, error, session, getToken, fromCache]);

    const getCustomToken = useCallback(async () => {
        const response = await fetch('/api/firebase/token', {
            headers: {
                authorization: `Bearer ${authToken}`,
            },
        });

        const data = await response.json();
        return data.firebaseToken;
    }, [authToken]);

    return useMemo(() => ({
        login: async () => {
            if (!authToken)
                return; // wait for token

            const token = await getCustomToken();

            signInWithCustomToken(auth, token)
                .catch((err) => error(err.message));
        },
        logout: async () => {
            await auth.signOut();
        },
    }), [authToken, getCustomToken, error]);
}

export function useDefaultFirebaseConverter<T extends object>() {
    const { session, fromCache } = useStytchMemberSession();

    const [orgId, setOrgId] = useState<string | null>(null);

    useEffect(() => {
        if (!session || fromCache)
            return; // wait for authentication

        setOrgId(session.organization_id ?? null);

    }, [setOrgId, session, fromCache]);

    const converter = useMemo(() => getConverter<T>(orgId), [orgId]);

    return !orgId ? null : converter;
}

export function getConverter<T extends object>(orgId: string | null) {
    return {
        toFirestore: (t: PartialWithFieldValue<T>) => {
            return {
                ...Object.entries(t).reduce((acc, [key, value]) => {
                    if (value instanceof Set) {
                        acc[key] = Array.from(value);
                    }
                    else if (value instanceof Map) {
                        acc[key] = Object.fromEntries(value);
                    }
                    else if (key.toLowerCase() === 'id') {
                        return acc;
                    } else {
                        acc[key] = value;
                    }
                    return acc
                }, {} as Record<string, unknown>),
                orgId,
                updatedAt: serverTimestamp(),
            }
        },
        fromFirestore: (
            snapshot: QueryDocumentSnapshot<T>,
            options?: SnapshotOptions
        ) : T => {
            const data = snapshot.data(options)!;
            return {
                id: snapshot.id,
                ref: snapshot.ref,
                ...data,
            } as T;
        },
    } as FirestoreDataConverter<T>
}
